import { Component, forwardRef, Input } from '@angular/core';
import { NG_VALUE_ACCESSOR } from '@angular/forms';
import { Color } from '../../../constants/color.constants';
import { Icons } from '../../../constants/icons.constants';
import { BaseFormControlComponent } from '../base-form-control.component';

/**
 * @group Forms
 * @component Checkbox
 *
 * @description A custom checkbox.
 * @export
 * @class LsqCheckboxComponent
 * @extends BaseFormControlComponent
 */
@Component({
    selector: 'lsq-checkbox',
    template: `
        <div
            [id]="createId()"
            [class.disabled]="disabled"
            [class.display-only]="displayOnly"
            [class.checkbox-container]="true"
        >
            <label class="checkbox-label">
                <!-- Make sure all SVGs are downloaded so that there isn't lag when swapping states -->
                <span [ngStyle]="{ position: 'absolute', visibility: 'hidden' }">
                    <lsq-icon [type]="icons.CheckboxCheckmark"></lsq-icon>
                    <lsq-icon [type]="icons.CheckboxDash"></lsq-icon>
                </span>

                <div
                    class="checkbox-icon"
                    [class.unchecked]="unchecked"
                    [class.checked]="checked"
                    [class.partially-checked]="partiallyChecked"
                    [class.disabled]="disabled"
                    [class.checked-disabled]="checkedDisabled"
                    [class.display-only]="displayOnly"
                    [class.centered]="centered"
                >
                    <lsq-icon
                        *ngIf="checked || checkedDisabled"
                        [type]="icons.CheckboxCheckmark"
                    ></lsq-icon>
                    <lsq-icon *ngIf="partiallyChecked" [type]="icons.CheckboxDash"></lsq-icon>
                </div>

                <input
                    [id]="createId('input')"
                    [attr.type]="'checkbox'"
                    [checked]="model"
                    [disabled]="disabled"
                    (change)="handleCheckboxChange()"
                />

                <span
                    *ngIf="label"
                    [title]="truncateLabelTitle ? label : ''"
                    [ngClass]="{ 'checkbox-label-contents': true, 'text-truncate': truncateLabel }"
                >
                    {{ label }}
                </span>
            </label>
        </div>
    `,
    styleUrls: ['./lsq-checkbox.component.scss'],
    providers: [
        {
            provide: NG_VALUE_ACCESSOR,
            useExisting: forwardRef(() => LsqCheckboxComponent),
            multi: true
        }
    ]
})
export class LsqCheckboxComponent extends BaseFormControlComponent<boolean> {
    private static EMPTY_STRING = '';

    @Input() public id: string;
    @Input() public label?: string;
    @Input() public labelClass?: string;
    @Input() public disabled = false;
    @Input() public partiallyChecked = false;
    @Input() public displayOnly = false;
    @Input() public truncateLabel = false;
    @Input() public truncateLabelTitle = false;
    @Input() public centered = false;

    public readonly icons = Icons;
    public readonly color = Color;

    public createId(suffix: string = LsqCheckboxComponent.EMPTY_STRING): string {
        if (!this.id) {
            return LsqCheckboxComponent.EMPTY_STRING;
        }
        return this.id + (suffix ? '-' + suffix : LsqCheckboxComponent.EMPTY_STRING);
    }

    public handleCheckboxChange(): void {
        const newValue = !this.model;
        this.handleChange(newValue);
    }
    public get unchecked(): boolean {
        return !this.model && !this.partiallyChecked;
    }
    public get checked(): boolean {
        return this.model && !this.partiallyChecked;
    }
    public get checkedDisabled(): boolean {
        return this.disabled && this.model;
    }
}
